import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { useSearch } from "./contexts/SearchContext";

export default function AdminUserDashboard() {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;
  const navigate = useNavigate();
  const { logAdminActivity } = useActivityLogger();
  const { searchQuery } = useSearch();

  useEffect(() => {
    logAdminActivity(
      "view",
      "view_admin_user_list",
      "user",
      null,
      "admin",
      "success",
      "",
      "Admin membuka daftar pengguna"
    );
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/admins/users");
      setUsers(res.data);
    } catch (err) {
      console.error("Gagal mengambil data admin", err);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.nomor_hp?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />

        <div className="bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              User {searchQuery && `(${filteredUsers.length} hasil pencarian)`}
            </h2>
          </div>

          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="py-2 text-left">Username</th>
                <th className="py-2 text-left">E-mail</th>
                <th className="py-2 text-left">No. HP</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((user) => (
                <tr key={user.ID} className="border-b">
                  <td className="py-4">{user.username}</td>
                  <td className="py-4">{user.email}</td>
                  <td className="py-4">{user.nomor_hp}</td>
                  <td className="py-4 align-middle">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          navigate(`/admin/users/${user.ID}/edit`)
                        }
                      >
                        <img
                          src="/icon-edit.png"
                          alt="Edit"
                          className="h-5 w-5"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && searchQuery && (
            <div className="text-center py-8 text-gray-500">
              Tidak ada hasil untuk "{searchQuery}"
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-end mt-4 gap-1">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                className="border px-2 py-1 rounded"
              >
                &lt;
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`border px-2 py-1 rounded ${
                    currentPage === i + 1 ? "bg-blue-500 text-white" : ""
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                className="border px-2 py-1 rounded"
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}